/**
 * The two reads a surface describing ONE movement makes, as `queryOptions`.
 *
 * Both hang off `exerciseLibraryRoot`, so creating, editing or deleting an exercise drops them with
 * the catalogue they describe — one invalidation, not three that have to be remembered together.
 *
 * ── `similar` is the backend's own answer, not a client-side filter ─────────────────────────
 * `GET /exercise/{id}/similar` returns at most eight movements sharing the type and a primary
 * muscle group. Deriving it here from the catalogue already in the cache was considered and
 * rejected: the rule is the backend's and the phone reads the same endpoint, so two clients
 * disagreeing about what is similar to a bench press would be two different products.
 *
 * `lang` travels for the reason every catalogue read carries it — the names come back localised and
 * a blank one silently resolves to English.
 *
 * ── The overlay's `retry: 0`, and why a 403 is an ANSWER ────────────────────────────────────
 * `GET /exercise/{id}/overlay` is refused with 403 on the trainer's OWN exercise, by design: an
 * overlay is something you put on somebody else's movement, and your own links live on the row
 * itself. So the failure is not a failure — it is the server saying "this question does not apply
 * here" — and retrying it three times is three requests to be told the same thing, with the caller
 * left on `isPending` through all of them. One attempt, and the caller reads the status.
 *
 * ── Why the overlay is read at all, when `GET /exercise` already resolves the links ─────────
 * They answer different questions. The catalogue's `links` are what the trainer SEES, library links
 * and overlay collapsed into one list. The overlay says which of the two that is: `null` means the
 * library's own links are showing, `[]` means the trainer has overridden them to none, and a list
 * means they replaced them. „Przywróć wersję z biblioteki" can only be offered off that
 * distinction, and no amount of looking at the resolved array recovers it.
 */

import { queryOptions } from '@tanstack/react-query';

import {
  getExerciseIdOverlay,
  getExerciseIdSimilar,
} from '~/shared/api/generated/endpoints/exercise/exercise';

import { exerciseLibraryRoot } from './exerciseQueries';
import type { ContentLanguage } from '~/shared/i18n/contentLanguage';

/**
 * At most eight movements of the same type sharing a primary muscle group.
 *
 * Thirty minutes, the same window the catalogue keeps: the answer changes only when the catalogue
 * does, and a trainer walking the theatre's prev/next pair must not refetch the strip per step.
 */
export function exerciseSimilarQueryOptions(id: number, lang: ContentLanguage) {
  return queryOptions({
    queryKey: [...exerciseLibraryRoot, 'similar', id, lang] as const,
    queryFn: ({ signal }) => getExerciseIdSimilar(id, { lang }, undefined, signal),
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Whether this trainer has overridden the movement's materials, and with what.
 *
 * No `lang`: the response carries addresses and the trainer's own labels, and nothing the server
 * translates. No `staleTime` either — this is the one thing on the screen the trainer edits, so it
 * is refetched on mount rather than served from a half-hour-old copy of a list they have since
 * changed on their phone.
 */
export function exerciseOverlayQueryOptions(id: number) {
  return queryOptions({
    queryKey: [...exerciseLibraryRoot, 'overlay', id] as const,
    queryFn: ({ signal }) => getExerciseIdOverlay(id, undefined, signal),
    // A 403 is the server answering "not applicable", not a transport failure. See the header.
    retry: 0,
  });
}
